import { fetchAuthSession } from "aws-amplify/auth";
import {
  clearPatientSession,
  currentPatientSession,
  isPatientPagePath,
  patientLoginPath,
  refreshPatientSession,
} from "../features/patient-auth";
import { isPatientSelfAuthEnabled } from "./featureFlags";
import { redirectToHostedUI } from "./hostedUi";
import { groupsOfAmplifySession, hasStaffGroup } from "./staffGroups";

const BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8080/api";

const resolveUrl = (url: string): string => {
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${BASE}${url}`;
  return `${BASE}/${url}`;
};

/** http の第 3 引数。orval の生成のフックは渡さない。 */
export type HttpBehavior = {
  /** false なら、401 で取り替えもログインの画面への移動もせずにそのまま投げる。既定は true */
  handleUnauthorized?: boolean;
  /**
   * false なら、401 で患者のトークンの取り替えと送り直しはするが、ログインの画面（患者は /patient/login、
   * スタッフは Hosted UI）へは移さず、セッションも消さずに投げる（ログアウトの後始末）。既定は true
   */
  redirectOnUnauthorized?: boolean;
  /**
   * "patient" なら、画面の URL によらず SAS の患者のセッションのトークンを付け、401 は患者のページと同じに扱う
   * （web のフラグが ON のときだけ。患者のページ以外、たとえばエラーの画面の PC のヘッダーからのログアウト）
   */
  credential?: "patient";
};

const tokenString = (raw: any): string | null =>
  typeof raw === "string"
    ? raw
    : typeof raw?.toString === "function"
      ? raw.toString()
      : typeof raw?.token === "string"
        ? raw.token
        : null;

/**
 * Amplify（Cognito）のアクセストークン。患者のフラグが ON なら、スタッフのグループを持つセッションのときだけ返す
 * （患者は SAS のトークンを使い、Cognito の患者のトークンは送らない）。
 */
const amplifyAccessToken = async (
  selfAuthEnabled: boolean,
): Promise<string | null> => {
  try {
    const session = await fetchAuthSession();
    if (selfAuthEnabled && !hasStaffGroup(groupsOfAmplifySession(session))) {
      return null;
    }
    return tokenString(session?.tokens?.accessToken ?? null);
  } catch {
    return null;
  }
};

const withAuthorization = (
  input: HeadersInit | undefined,
  token: string | null,
): Headers => {
  const headers = new Headers(input);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return headers;
};

const unauthorizedError = async (res: Response): Promise<any> => {
  const bodyText = await res.text().catch(() => "");
  let error: any = { status: 401, title: "Unauthorized" };
  try {
    if (bodyText) error = { ...error, ...JSON.parse(bodyText) };
  } catch {}
  return error;
};

// 患者のページの 401 でログインの画面へ移すのは 1 回だけにする（同時の 401 で assign を重ねない）
let leavingForPatientLogin = false;
const leaveForPatientLogin = (path: string): void => {
  if (leavingForPatientLogin) return;
  leavingForPatientLogin = true;
  window.location.assign(path);
};

export async function http<TData = unknown, TError = unknown>(
  url: string,
  options?: RequestInit,
  behavior?: HttpBehavior,
): Promise<TData> {
  const fullUrl = resolveUrl(url);
  const handleUnauthorized = behavior?.handleUnauthorized ?? true;
  const redirect =
    handleUnauthorized && (behavior?.redirectOnUnauthorized ?? true);
  const selfAuthEnabled = isPatientSelfAuthEnabled();
  // トークンの選び方と 401 の扱いは、API の URL でなく、要求を出した画面で決める（明示の指定があればそれに従う）
  const patientPage =
    selfAuthEnabled &&
    (behavior?.credential === "patient" ||
      isPatientPagePath(window.location.pathname));

  const send = (token: string | null) =>
    fetch(fullUrl, {
      ...options,
      headers: withAuthorization(options?.headers, token),
      credentials: options?.credentials ?? "include",
    });

  const sentPatientToken = patientPage
    ? (currentPatientSession()?.accessToken ?? null)
    : null;
  let res = await send(
    patientPage ? sentPatientToken : await amplifyAccessToken(selfAuthEnabled),
  );

  if (res.status === 401 && handleUnauthorized && patientPage) {
    res = await retryPatientRequest(res, sentPatientToken, send, redirect);
  }

  if (res.status === 401) {
    if (redirect && !patientPage) {
      try {
        await redirectToHostedUI();
      } catch {}
    }
    throw (await unauthorizedError(res)) as TError;
  }

  const bodyText = [204, 205, 304].includes(res.status)
    ? ""
    : await res.text().catch(() => "");
  if (!res.ok) {
    let error: any = { status: res.status };
    try {
      if (bodyText) error = { ...error, ...JSON.parse(bodyText) };
    } catch {}
    throw error as TError;
  }

  const data = bodyText ? JSON.parse(bodyText) : ({} as any);
  const result: any = { data, status: res.status, headers: res.headers };
  return result as TData;
}

/**
 * 患者のページの 401（web のフラグが ON）。Cognito の画面へは送らない。
 *
 * - SAS のセッションが無いまま送った要求なら、notice を付けずにログインの画面へ移す
 * - あれば 1 回だけ取り替えて送り直す（ほかの要求が先に取り替えていれば、その新しいトークンで送り直す）
 * - 取り替えを受け付けない（セッションが消えた）か、送り直しも 401 なら、セッションを消して notice=expired で移す
 * - 取り替えの 5xx・通信の失敗は、セッションを消さず、移さずに投げる
 *
 * 移したときも 401 の応答を返し、呼び手が例外を投げる（画面は移るので、例外の文言は利用者に見えない）。
 * redirect が false なら、取り替えと送り直しまでにし、セッションを消さず、移さない（取り替えの 400・404 で
 * refreshPatientSession が消すのは同じ）。
 */
async function retryPatientRequest(
  unauthorized: Response,
  sentToken: string | null,
  send: (token: string | null) => Promise<Response>,
  redirect: boolean,
): Promise<Response> {
  if (sentToken === null) {
    if (redirect) leaveForPatientLogin(patientLoginPath());
    return unauthorized;
  }
  const latest = currentPatientSession();
  const session =
    latest !== null && latest.accessToken !== sentToken
      ? latest
      : await refreshPatientSession();
  if (session === null) {
    if (redirect) {
      clearPatientSession();
      leaveForPatientLogin(patientLoginPath("expired"));
    }
    return unauthorized;
  }
  const retried = await send(session.accessToken);
  if (retried.status === 401 && redirect) {
    clearPatientSession();
    leaveForPatientLogin(patientLoginPath("expired"));
  }
  return retried;
}
